import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useAddSubCategoryMutation,
  useDeleteSubCategoryMutation,
  useAddSubSubCategoryMutation,
} from "state/api";
import { PageContainer, TableToolbar } from "components/shared";

const PRIORITIES = [1, 2, 3, 4, 5];

// Single "Category Manager" replacing the former category / sub-category /
// sub-sub-category pages. Renders the Category → subCategories → subSubCategories
// tree as an expandable master/detail list with inline add/edit/delete at each
// level. All data comes from the real category API — no mock data.
const CategoryManager = () => {
  const [search, setSearch] = useState("");
  const { data, isFetching } = useGetCategoriesQuery({ search });
  const categories = useMemo(() => data?.data || [], [data]);

  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [addSubCategory] = useAddSubCategoryMutation();
  const [deleteSubCategory] = useDeleteSubCategoryMutation();
  const [addSubSubCategory] = useAddSubSubCategoryMutation();

  // Which category / sub-category rows are expanded.
  const [expandedCat, setExpandedCat] = useState({});
  const [expandedSub, setExpandedSub] = useState({});

  // Dialog state: { mode, title, fields, initial, onSubmit }.
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null); // { severity, message }
  const notify = (severity, message) => setToast({ severity, message });

  const toggleCat = (id) =>
    setExpandedCat((s) => ({ ...s, [id]: !s[id] }));
  const toggleSub = (id) =>
    setExpandedSub((s) => ({ ...s, [id]: !s[id] }));

  // ── Dialog helpers ──────────────────────────────────────────────────────
  const openDialog = (config) => {
    setForm(config.initial || { name: "", priority: 1 });
    setDialog(config);
  };
  const closeDialog = () => {
    setDialog(null);
    setForm({});
  };

  const submitDialog = async () => {
    if (!dialog) return;
    if (!String(form.name || "").trim()) {
      notify("error", "Name is required");
      return;
    }
    setSaving(true);
    try {
      await dialog.onSubmit(form);
      notify("success", dialog.successMessage || "Saved");
      closeDialog();
    } catch (err) {
      notify("error", err?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // ── Action builders ─────────────────────────────────────────────────────
  const addCategory = () =>
    openDialog({
      title: "Add Category",
      successMessage: "Category created",
      initial: { name: "", priority: 1 },
      onSubmit: (f) =>
        createCategory({ name: f.name.trim(), priority: f.priority }).unwrap(),
    });

  const editCategory = (cat) =>
    openDialog({
      title: "Edit Category",
      successMessage: "Category updated",
      initial: { name: cat.name, priority: cat.priority || 1 },
      onSubmit: (f) =>
        updateCategory({
          id: cat._id,
          name: f.name.trim(),
          priority: f.priority,
        }).unwrap(),
    });

  const addSub = (cat) =>
    openDialog({
      title: `Add Sub-category to "${cat.name}"`,
      successMessage: "Sub-category added",
      initial: { name: "", priority: 1 },
      onSubmit: (f) =>
        addSubCategory({
          categoryId: cat._id,
          name: f.name.trim(),
          priority: f.priority,
        }).unwrap(),
    });

  const addSubSub = (cat, sub) =>
    openDialog({
      title: `Add Sub-sub-category to "${sub.name}"`,
      successMessage: "Sub-sub-category added",
      initial: { name: "", priority: 1 },
      onSubmit: (f) =>
        addSubSubCategory({
          categoryId: cat._id,
          subId: sub._id,
          name: f.name.trim(),
          priority: f.priority,
        }).unwrap(),
    });

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}" and all its children?`))
      return;
    try {
      await deleteCategory(cat._id).unwrap();
      notify("success", "Category deleted");
    } catch (err) {
      notify("error", err?.data?.message || "Failed to delete category");
    }
  };

  const removeSub = async (cat, sub) => {
    if (!window.confirm(`Delete sub-category "${sub.name}"?`)) return;
    try {
      await deleteSubCategory({ categoryId: cat._id, subId: sub._id }).unwrap();
      notify("success", "Sub-category deleted");
    } catch (err) {
      notify("error", err?.data?.message || "Failed to delete sub-category");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <PageContainer
      title="Categories"
      subtitle="Manage categories, sub-categories and sub-sub-categories in one place"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={addCategory}>
          Add Category
        </Button>
      }
    >
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search categories…"
      />

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={22} />
          </Box>
        ) : categories.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8,
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No categories found</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {categories.map((cat, ci) => {
              const subs = cat.subCategories || [];
              const open = !!expandedCat[cat._id];
              return (
                <Box key={cat._id}>
                  {ci > 0 && <Divider />}
                  <ListItem
                    sx={{ py: 1.25 }}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Add sub-category">
                          <IconButton
                            size="small"
                            onClick={() => addSub(cat)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => editCategory(cat)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeCategory(cat)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleCat(cat._id)}
                      sx={{ mr: 1 }}
                    >
                      {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </IconButton>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {cat.name}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Priority ${cat.priority || 1}`}
                          />
                          <Chip
                            size="small"
                            label={`${subs.length} sub`}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                    />
                  </ListItem>

                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 6, pr: 2, pb: 1, bgcolor: "action.hover" }}>
                      {subs.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 1.5 }}
                        >
                          No sub-categories yet.
                        </Typography>
                      ) : (
                        <List disablePadding>
                          {subs.map((sub) => {
                            const subSubs = sub.subSubCategories || [];
                            const subOpen = !!expandedSub[sub._id];
                            return (
                              <Box key={sub._id}>
                                <ListItem
                                  sx={{ py: 0.5 }}
                                  secondaryAction={
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                      <Tooltip title="Add sub-sub-category">
                                        <IconButton
                                          size="small"
                                          onClick={() => addSubSub(cat, sub)}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => removeSub(cat, sub)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleSub(sub._id)}
                                    sx={{ mr: 1 }}
                                  >
                                    {subOpen ? (
                                      <ExpandMoreIcon fontSize="small" />
                                    ) : (
                                      <ChevronRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  <ListItemText
                                    primary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography sx={{ fontWeight: 500 }}>
                                          {sub.name}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`P${sub.priority || 1}`}
                                        />
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`${subSubs.length} sub-sub`}
                                        />
                                      </Box>
                                    }
                                  />
                                </ListItem>

                                <Collapse
                                  in={subOpen}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ pl: 6, pb: 1 }}>
                                    {subSubs.length === 0 ? (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ py: 1 }}
                                      >
                                        No sub-sub-categories yet.
                                      </Typography>
                                    ) : (
                                      <List disablePadding dense>
                                        {subSubs.map((ss) => (
                                          <ListItem key={ss._id} sx={{ py: 0.25 }}>
                                            <SubdirectoryArrowRightIcon
                                              fontSize="small"
                                              sx={{
                                                mr: 1,
                                                color: "text.disabled",
                                              }}
                                            />
                                            <ListItemText
                                              primary={
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {ss.name}
                                                  </Typography>
                                                  <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`P${ss.priority || 1}`}
                                                  />
                                                </Box>
                                              }
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    )}
                                  </Box>
                                </Collapse>
                              </Box>
                            );
                          })}
                        </List>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Add / edit dialog (shared for every level) */}
      <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>{dialog?.title}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={form.name || ""}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitDialog();
            }}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            label="Priority"
            value={form.priority || 1}
            onChange={(e) =>
              setForm((f) => ({ ...f, priority: e.target.value }))
            }
          >
            {PRIORITIES.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitDialog} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PageContainer>
  );
};

export default CategoryManager;
